import { Typography } from '@euler/app/components/typography';
import { useQuotationNavigation } from '@euler/app/flows/task/pages/quotation/functions/useQuotationNavigation';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { formatMoney } from '@euler/utils/formatMoney';
import { memo, useCallback } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import invariant from 'tiny-invariant';

export const DiscountButton = memo(
  ({
    label,
    price,
    discountPrice,
    discount,
    editable,
    onDiscountChange,
    style,
  }: {
    label: string;
    price: number | undefined;
    discountPrice: number | undefined;
    discount?: number;
    editable?: boolean;
    style?: StyleProp<ViewStyle>;
    onDiscountChange?: (discount: number) => void;
  }) => {
    const navigation = useQuotationNavigation();

    const { rpx } = useResponsiveContext();

    const originalPrice = discountPrice ?? price;

    const onPress = useCallback(() => {
      const description =
        discountPrice != null && discountPrice !== price
          ? `在当前折后价${discountPrice}元(原价${price}元)基础上设置额外优惠`
          : `在当前价${price}元基础上设置额外优惠`;
      navigation.navigate('_discount', {
        title: `设置${label}优惠`,
        defaultValue: discount,
        message: description,
        onConfirm: (value, mode) => {
          invariant(originalPrice != null);

          let updatedDiscount = 0;

          if (mode === 'amount') {
            updatedDiscount = value;
          } else {
            updatedDiscount = (originalPrice * value) / 100;
          }

          if (updatedDiscount > originalPrice) {
            throw new Error(
              `优惠金额不能超过${formatMoney(originalPrice * 100)}元!`,
            );
          }

          if (updatedDiscount !== (discount ?? 0)) {
            onDiscountChange?.(updatedDiscount);
          }
        },
      });
    }, [
      discount,
      discountPrice,
      label,
      navigation,
      onDiscountChange,
      originalPrice,
      price,
    ]);

    if (!discount && !editable) {
      return null;
    }

    let text = '折';

    if (originalPrice == null || originalPrice === 0) {
      return null;
    }

    if (discount != null && discount > 0) {
      text = `${(((originalPrice - discount) / originalPrice) * 10)
        .toFixed(1)
        .replace(/\.0+$/, '')}折`;
    }

    return (
      <Center.Touchable
        style={style}
        hitSlop={PredefinedHitSlops.extraLarge}
        disabled={!editable}
        onPress={onPress}
        css={`
          background-color: ${discount != null && discount > 0
            ? StatusColors.Info
            : '#999'};
          border-radius: 100px;
          min-width: ${rpx(16)}px;
          height: ${rpx(16)}px;
          padding: ${rpx(2)}px ${rpx(3)}px;
        `}
      >
        <Typography.Text color="#fff" size={rpx(9)} lineHeight={rpx(12)}>
          {text}
        </Typography.Text>
      </Center.Touchable>
    );
  },
);
